import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders, getUser } from '../utils/auth';
import Sidebar from '../components/Sidebar';
import { FileText, Clock, CheckCircle, XCircle, AlertTriangle, Search } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const user = getUser();
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    closed: 0,
    rejected: 0,
    needs_revision: 0
  });
  const [allPTWs, setAllPTWs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchAdminData();
  }, []);

  const fetchAdminData = async () => {
    try {
      const [statsRes, ptwsRes] = await Promise.all([
        axios.get(`${API}/ptw/admin/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/ptw/admin/all`, { headers: getAuthHeaders() })
      ]);
      setStats(statsRes.data);
      setAllPTWs(ptwsRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      if (error.response?.status === 403) {
        toast.error('Anda tidak memiliki akses ke halaman ini');
        navigate('/dashboard');
      } else {
        toast.error('Gagal memuat data');
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', label: 'Draft' },
      waiting_high_risk_forms: { color: 'bg-yellow-100 text-yellow-800', label: 'Waiting High Risk Forms' },
      waiting_pic: { color: 'bg-blue-100 text-blue-800', label: 'Waiting PIC' },
      waiting_area_manager: { color: 'bg-blue-100 text-blue-800', label: 'Waiting Area Manager' },
      waiting_she: { color: 'bg-blue-100 text-blue-800', label: 'Waiting SHE' },
      waiting_management: { color: 'bg-blue-100 text-blue-800', label: 'Waiting Leadership Team' },
      waiting_closing: { color: 'bg-purple-100 text-purple-800', label: 'Waiting Closing' },
      waiting_closing_approval: { color: 'bg-purple-100 text-purple-800', label: 'Waiting Closing Approval' },
      needs_revision: { color: 'bg-orange-100 text-orange-800', label: 'Needs Revision' },
      approved: { color: 'bg-green-100 text-green-800', label: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected' },
      closed: { color: 'bg-gray-100 text-gray-800', label: 'Closed' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const filteredPTWs = allPTWs.filter(ptw => {
    const matchesSearch = 
      ptw.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ptw.lokasi_pekerjaan?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ptw.requester_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ptw.deskripsi_pekerjaan?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || ptw.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 lg:ml-64 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      
      <main className="flex-1 lg:ml-64 overflow-y-auto">
        <div className="p-6 lg:p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Admin Dashboard</h1>
            <p className="text-gray-600">Overview semua PTW dari semua vendor</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FileText className="text-blue-600" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.total}</p>
                  <p className="text-xs text-gray-500">Total PTW</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <Clock className="text-yellow-600" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.pending}</p>
                  <p className="text-xs text-gray-500">Pending</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="text-green-600" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.approved}</p>
                  <p className="text-xs text-gray-500">Approved</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <CheckCircle className="text-gray-600" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.closed}</p>
                  <p className="text-xs text-gray-500">Closed</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <XCircle className="text-red-600" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.rejected}</p>
                  <p className="text-xs text-gray-500">Rejected</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <AlertTriangle className="text-orange-600" size={24} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.needs_revision}</p>
                  <p className="text-xs text-gray-500">Needs Revision</p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search by ID, lokasi, requester, atau deskripsi..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full md:w-48 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">Semua Status</option>
                  <option value="waiting_pic">Waiting PIC</option>
                  <option value="waiting_area_manager">Waiting Area Manager</option>
                  <option value="waiting_she">Waiting SHE</option>
                  <option value="waiting_management">Waiting Leadership Team</option>
                  <option value="waiting_closing">Waiting Closing</option>
                  <option value="waiting_closing_approval">Waiting Closing Approval</option>
                  <option value="needs_revision">Needs Revision</option>
                  <option value="approved">Approved</option>
                  <option value="closed">Closed</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
            </div>
          </div>

          {/* PTW Table */}
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-800">
                Semua PTW ({filteredPTWs.length})
              </h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lokasi</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requester</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">PIC</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">High Risk</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tanggal</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredPTWs.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-4 py-12 text-center text-gray-500">
                        {searchTerm || statusFilter !== 'all' ? 'Tidak ada PTW yang sesuai filter' : 'Belum ada PTW'}
                      </td>
                    </tr>
                  ) : (
                    filteredPTWs.map((ptw) => (
                      <tr key={ptw.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-mono text-gray-900">{ptw.id.substring(0, 8)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{ptw.lokasi_pekerjaan}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{ptw.requester_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{ptw.pic_name || '-'}</td>
                        <td className="px-4 py-3">{getStatusBadge(ptw.status)}</td>
                        <td className="px-4 py-3">
                          {ptw.high_risk_activities && ptw.high_risk_activities.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {ptw.high_risk_activities.map((activity, idx) => (
                                <span key={idx} className="px-1.5 py-0.5 bg-red-100 text-red-800 rounded text-xs">
                                  ⚠️ {activity.substring(0, 10)}...
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{ptw.tanggal_mulai}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => navigate(`/ptw/${ptw.id}`)}
                            className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
